import { useEffect, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { footerContacts, useCaseSite } from '../app/caseSite';
import { useSession } from '../app/session';
import { useSite } from '../app/site';
import { useNotifications } from '../features/notifications/useNotifications';
import { useT } from '../i18n';
import { useOnVisible } from '../lib/useAsync';
import { LanguagePicker } from './LanguagePicker';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

export function Layout({ children }: { children: ReactNode }) {
  const t = useT();
  const authed = useSession((s) => s.status === 'authed');
  const site = useSite((s) => s.site);
  const caseSite = useCaseSite((s) => s.site);
  const unread = useNotifications((s) => s.unread);
  const refreshCount = useNotifications((s) => s.refreshCount);
  const resetNotifications = useNotifications((s) => s.reset);
  const { pathname } = useLocation();
  // контакти сайту справи, яку зараз переглядають (MD-справа → молдовські), інакше — сайту кабінету;
  // порожні значення не показуємо
  const contacts = footerContacts(caseSite, site);
  const navCls = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-tap items-center gap-2 whitespace-nowrap border-b pb-[2px] text-[15px] transition-colors ${isActive ? 'border-fg text-fg' : 'border-transparent text-lab hover:text-fg'}`;

  useEffect(() => {
    if (authed) void refreshCount();
    else resetNotifications();
  }, [authed, pathname, refreshCount, resetNotifications]);
  useOnVisible(() => {
    if (useSession.getState().status === 'authed') void refreshCount();
  });

  return (
    <div className="flex min-h-[100dvh] flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-bg focus:p-3">
        {t('app.skip')}
      </a>
      <header className="wrap flex items-center justify-between gap-3 pt-6 sm:pt-10">
        <Link to={authed ? '/cases' : '/login'} className="min-h-tap py-1" aria-label={t('nav.cases')}>
          <Logo caption={t('common.cabinet')} />
        </Link>
        <div className="flex items-center gap-1">
          <LanguagePicker />
          <ThemeToggle />
        </div>
      </header>
      {authed ? (
        <nav aria-label={t('nav.main')} className="wrap mt-6">
          <div className="flex gap-x-5 overflow-x-auto border-b border-line sm:gap-x-7">
            <NavLink to="/cases" className={navCls}>
              {t('nav.cases')}
            </NavLink>
            <NavLink to="/notifications" className={navCls}>
              {t('nav.notifications')}
              {unread > 0 ? (
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-fg px-1.5 text-[11px] leading-none text-bg"
                  aria-label={t('notif.unread', { n: unread })}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </NavLink>
            <NavLink to="/privacy" className={navCls}>
              {t('nav.privacy')}
            </NavLink>
          </div>
        </nav>
      ) : null}
      <main id="main" tabIndex={-1} className="wrap flex-1 pb-16 pt-8 outline-none sm:pt-12">
        {children}
      </main>
      {contacts.phone || contacts.email ? (
        <footer className="wrap border-t border-line py-6 text-[13px] text-lab" data-testid="site-footer">
          <p className="sr-only">{t('site.contacts')}</p>
          <div className="flex flex-wrap items-center gap-x-2">
            {contacts.phone ? (
              <>
                <a href={`tel:${contacts.phone.replace(/[^\d+]/g, '')}`} className="inline-flex min-h-tap items-center text-txt hover:text-fg">
                  {contacts.phone}
                </a>
                {contacts.duty ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{t('privacy.duty')}</span>
                  </>
                ) : null}
              </>
            ) : null}
            {contacts.phone && contacts.email ? <span aria-hidden>·</span> : null}
            {contacts.email ? (
              <a href={`mailto:${contacts.email}`} className="inline-flex min-h-tap items-center text-txt hover:text-fg">
                {contacts.email}
              </a>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  );
}
